import * as React from 'react';
import { NextPage } from 'next';
import Image from 'next/image';
import { NextSeo } from 'next-seo';
import { Type } from '@/analytics/client';
import { Button } from '@/components/ui';
import { useAnalyticsDispatch } from '@/context/analytics';
import { useEpixelClick } from '@/hooks/useEpixelClick';

const BlogPage: NextPage = () => {
  const {
    buttonFunction,
    hasJoined
  } = useEpixelClick();
  const analyticsDispatch = useAnalyticsDispatch();

  function handleButtonClick() {
    analyticsDispatch({
      type: 'BUTTON_CLICKED',
      payload: {
        text: ctaText,
        location: 'collective',
        type: Type.Button
      }
    });
    buttonFunction();
  }

  const ctaText = hasJoined ? 'View Portal' : 'Join Now';
  return <>
      <NextSeo canonical='https://28.co/blog' description='New Research Confirms: Cycle-based Wellness Boosts Mental & Physical Well-Being' openGraph={{
      title: "How Cycle-Based Wellness With 28 Transforms Women's Health",
      description: 'New Research Confirms: Cycle-based Wellness Boosts Mental & Physical Well-Being',
      type: 'website',
      url: 'https://28.co/blog'
    }} title="How Cycle-Based Wellness With 28 Transforms Women's Health" />

      {
      /* Main Blog Header Section */
    }
      <div className='bg-[#F4F3E8]'>
        <div className='mx-auto px-6 md:px-12 flex flex-col items-center'>
          <div className='pt-12 md:pt-[80px] pb-12 md:pb-[80px]'>
            <p className='text-center text-[10px] font-bold tracking-[0.2em] uppercase text-[#45525F] mb-4'>STUDIES</p>
            <div className='text-center max-w-[1200px]'>
              <h1 className='font-bodoni font-normal text-[32px] md:text-6xl lg:text-[56px] leading-tight md:leading-[115%] text-[#45525F] mb-4' style={{
              textWrap: 'balance'
            }}>
                Scientific Research Shows How <br className='hidden lg:block' />
                28's Cycle-Based Wellness <br className='hidden lg:block' />
                Transforms Women's Health
              </h1>
              <p className='text-sm md:text-base text-[#45525F] mb-8 md:whitespace-nowrap mx-auto'>
                Groundbreaking research reveals the benefits of syncing exercise and nutrition with your cycle
              </p>
            </div>
            <div className='w-full flex justify-center scale-[1.3] sm:scale-100 origin-top overflow-x-auto sm:overflow-visible mb-20 sm:mb-0'>
              <Image alt='28 App Screenshots showing cycle-based wellness features' className='block w-full h-auto object-contain rounded-lg max-w-[380px] sm:max-w-full' height={1000} priority src='/images/temp/Rectangle1.png' width={800} />
            </div>
          </div>
        </div>
      </div>

      {
      /* Content Section */
    }
      <div className='bg-[#FCFBF6] py-8 md:py-16'>
        <div className='max-w-[1280px] mx-auto px-6 md:px-12'>
          <div className='mb-8 md:mb-16'>
            <h2 className='font-bodoni font-normal text-3xl md:text-5xl lg:text-[56px] leading-tight md:leading-[115%] text-[#45525F] mb-4 md:mb-6'>
              <span className='hidden lg:block'>New Research Confirms: Cycle-Based</span>
              <span className='hidden lg:block'>Wellness Boosts Physical and Mental Well-Being</span>

              {
              /* Tablet version */
            }
              <span className='hidden md:block lg:hidden'>New Research Confirms:</span>
              <span className='hidden md:block lg:hidden'>
                Cycle-Based Wellness Boosts Physical and Mental Well-Being
              </span>

              {
              /* Mobile version */
            }
              <span className='block md:hidden'>
                New Research Confirms: Cycle-Based Wellness Boosts Physical and Mental Well-Being
              </span>
            </h2>
            <p className='font-lato font-normal text-base leading-6 text-[#45525F] mb-6'>
              By Melissa M. Brillhart, Jeffrey Gassen, Lisa Lin, Gabriel Hugoboom, Brittany Hugoboom, and Sarah Hill
            </p>
            <div className='w-[68px] h-[26px] bg-[#45525F] rounded-lg flex items-center justify-center'>
              <span className='font-lato font-bold text-[10px] leading-[10px] tracking-[0.2em] uppercase text-[#FCFBF6]'>
                Studies
              </span>
            </div>
          </div>

          <div className='prose prose-lg max-w-none  space-y-16'>
            <p className='font-lato font-normal text-base leading-[24px]'>
              At 28 Wellness, we've always believed in the power of cycle-based wellness —and now, science backs it up.
              A new study (currently available on Open Science), led by Dr. Sarah Hill, finds that users of 28's
              cycle-based approach to nutrition and exercise report scoring better on many health and wellness measures
              than non-users.
              <br />
              <br />
              The study examined the impact of 28's cycle-based approach to fitness and nutrition, comparing women who
              follow <span className='italic'>28's Cycle-Based Wellness</span> routine to those who don't. The results
              are compelling: cycle-based wellness significantly improves mental and physical health.
            </p>

            <div>
              <h3 className='font-bodoni font-normal text-[32px] md:text-3xl lg:text-[40px] leading-[100%] tracking-[0px] md:leading-[115%] text-[#45525F] mb-4 md:mb-8'>
                The Results:
              </h3>
              <div className='space-y-4'>
                <p>
                  <span className='font-bold'>1. Happier Mind, Healthier Body:</span> Women who followed 28's Cycle
                  Based Wellness reported higher happiness levels, lower stress, and reduced anxiety and depression
                  compared to those who followed a standard fitness routine.
                </p>
                <p>
                  <span className='font-bold'>2. Stronger Body Image & Fitness:</span> Participants who followed 28's
                  Cycle Based Wellness felt more confident in their bodies, more attractive, and overall healthier and
                  fitter.
                </p>
                <p>
                  <span className='font-bold'>3. Better Cycle Health:</span> Women who followed 28's Cycle Based
                  Wellness routine experienced more regular menstrual cycles, increased energy levels, fewer food
                  cravings, and better control over eating habits.
                </p>
                <p>
                  <span className='font-bold'>4. Reduced Reliance on Doctors:</span> Those following 28's Cycle Based
                  Wellness for at least three months reported a lower need to consult a doctor for menstrual-related
                  symptoms and a better understanding of when medical care is truly necessary.
                </p>
                <p>
                  <span className='font-bold'>5. Longer Practice, Greater Benefits:</span> A follow-up study found that
                  women who practiced 28's Cycle Based Wellness for six months or more saw even greater improvements in
                  mental clarity, physical well-being, and hormonal balance.
                </p>
                <br />
                <p>
                  These findings reinforce what our 28 community has shared the past two years: tuning into your cycle
                  daily is a game-changer, and the 28 app is the first and only scientifically validated cycle-based
                  wellness method.
                </p>
              </div>
            </div>

            <div>
              <h3 className='font-bodoni font-normal text-[32px] md:text-3xl lg:text-[40px] leading-tight tracking-[0px] md:leading-[115%] text-[#45525F] mb-4 md:mb-8'>
                Other Important Things That We Learned From This Study?
              </h3>
              <p className='mb-6'>Since they have started using 28, more than:</p>
              <div className='space-y-6'>
                <p>
                  <span className='font-bold'>• 95% </span>of women said that they have felt healthier
                </p>
                <p>
                  <span className='font-bold'>• 93% </span>of women said that they have become happier with their body
                  look and feel
                </p>
                <p>
                  <span className='font-bold'>• 91% </span>of women said that they have experienced less anxiety
                </p>
                <p>
                  <span className='font-bold'>• 90% </span>of women said that they have felt more energetic
                </p>
                <p>
                  <span className='font-bold'>• 90% </span>of women said that their cycles have become more regular
                </p>
                <p>
                  <span className='font-bold'>• 86% </span>of women said that they have gotten less period pain
                </p>
                <p>
                  <span className='font-bold'>• 86% </span>of women said that they have experienced less PMS
                </p>
                <p>
                  <span className='font-bold'>• 83% </span>of women said that they have gotten less long/heavy periods
                </p>
              </div>
              <p className='mt-8'>
                <span className='font-bold'>
                  "Although people have been talking for years about the idea of tailoring nutrition and exercise to the
                  unique needs of the cycle, no one has approached it in such a scientifically-grounded way,"
                </span>{' '}
                says Dr. Sarah E. Hill, 28's lead science advisor.{' '}
                <span className='font-bold'>
                  "And now that the data are coming in that demonstrate that it’s improving women’s lives, I think that
                  we can feel confident that 28's recommendations are working for women."
                </span>
              </p>
            </div>

            <div>
              <h3 className='font-bodoni font-normal text-[32px] md:text-3xl lg:text-[40px] leading-tight tracking-[0px] md:leading-[115%] text-[#45525F] mb-4 md:mb-8'>
                The Anatomy of Cycle-Based Wellness
              </h3>
              <p className='mb-6'>
                <span className='italic'>28's Cycle-Based Wellness</span> method is built on four key components: Body
                Literacy, Feminine Fitness, Hormone-Focused Nutrition, and Lifestyle Guidance.
              </p>
              <div className='space-y-6'>
                <p>
                  <span className='font-bold'>• Body Literacy:</span> Understanding what's happening in your body at any
                  given time is the foundation of <span className='italic'>Cycle-Based Wellness™. 28</span> breaks down
                  the science behind hormonal fluctuations, their impact on the body and brain, and how women can track
                  and interpret these changes to optimize well-being. This includes symptom documentation, cycle phase
                  identification, and insights into rising and falling hormones.
                </p>
                <p>
                  <span className='font-bold'>• Feminine Fitness:</span> Traditional fitness programs are built around
                  male physiology and a 24-hour hormone cycle, failing to account for the unique needs of women, whose
                  hormones fluctuate over a 28-day cycle. <span className='italic'>28's</span> fitness method—designed
                  by certified celebrity trainers from the Gray Institute—prioritizes low-impact, stability-based
                  movements that optimize hormonal balance by keeping cortisol levels low and adjusting intensity based
                  on the body's needs in each phase.
                </p>
                <p>
                  <span className='font-bold'>• Hormone-Focused Nutrition:</span> Developed by certified functional
                  nutritionists, <span className='italic'>28's</span> nutrition philosophy shifts away from restrictive
                  dieting and instead emphasizes nutrient-dense, unprocessed foods that deliver the key vitamins,
                  minerals, and macronutrients needed to support hormonal health at every stage of the cycle.
                </p>
                <p>
                  <span className='font-bold'>• Lifestyle Guidance:</span> Studies show that women's preferences, energy
                  levels, and cognitive patterns fluctuate throughout the cycle. <span className='italic'>28</span>{' '}
                  provides evidence-based insights and recommendations that align with these hormonal shifts to help
                  women work with their bodies rather than against them.
                </p>
              </div>
            </div>

            <div>
              <h3 className='font-bodoni font-normal text-[32px] md:text-3xl lg:text-[40px] leading-tight tracking-[0px] md:leading-[115%] text-[#45525F] mb-4 md:mb-8'>
                The Technology Behind 28
              </h3>
              <p className='mb-6'>
                To make <span className='italic'>Cycle-Based Wellness</span> truly personalized, 28 developed an
                advanced technology platform to deliver real-time, tailored recommendations. At the core of this system
                is a patent-pending algorithm—the most sophisticated content recommendation engine of its kind—combined
                with a highly accurate phase-specific menstrual cycle tracker. This proprietary technology ensures that
                every user's experience is dynamically adjusted in real time to meet their unique hormonal needs.
              </p>
              <p className='mb-4'>Want to dive deeper into the research? Read the full study:</p>
              <p className='mb-2'>
                Preprint:{' '}
                <a className='text-[#45525F] underline hover:text-opacity-80' href='https://osf.io/preprints/psyarxiv/39rwn_v1' rel='noopener noreferrer' target='_blank'>
                  https://osf.io/preprints/psyarxiv/39rwn_v1
                </a>
              </p>
              <p className='mb-6'>
                Pre-registration for the project:{' '}
                <a className='text-[#45525F] underline hover:text-opacity-80' href='https://osf.io/zutvp/' rel='noopener noreferrer' target='_blank'>
                  https://osf.io/zutvp/
                </a>
              </p>
              <p>
                This article was reviewed by{' '}
                <a className='text-[#45525F] underline hover:text-opacity-80' href='https://www.sarahehill.com/' rel='noopener noreferrer' target='_blank'>
                  Dr. Sarah E. Hill
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {
      /* Super CTA Section */
    }
      <div className='relative bg-cover bg-center h-[400px] sm:h-[600px] md:min-h-screen' style={{
      backgroundImage: "url('/images/temp/Image.jpg')"
    }}>
        <div className='absolute inset-0 bg-black bg-opacity-30 flex flex-col items-center justify-center z-10 text-center px-6 md:px-8'>
          <div className='max-w-[450px] md:max-w-[800px] lg:max-w-[1200px]'>
            <h2 className='font-bodoni font-normal leading-tight text-4xl sm:text-4xl md:text-6xl text-khaki-1 mb-4'>
              Stay In Sync, Sign Up!
            </h2>
            <p className='font-lato text-base md:text-lg text-khaki-1 mt-4'>
              Recipes, tips and inspiration, straight to your inbox.
            </p>
            <div className='w-full flex justify-center'>
              <Button className='mt-6 self-center z-20' onClick={handleButtonClick} variant='primary'>
                Sign Up
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>;
};

BlogPage.displayName = 'BlogPage';
export default BlogPage;